import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Save, Download, Play, Pause, SkipBack, SkipForward, FileText, Globe, Scissors, Monitor, Type, Droplets, Upload, Settings } from 'lucide-react';
import { useProject } from '../context/ProjectContext';
import VideoPlayer from '../components/VideoPlayer';
import TranscriptEditor from '../components/TranscriptEditor';
import TranslationPanel from '../components/TranslationPanel';
import PartsCutter from '../components/PartsCutter';
// 🎨 NEW: Modern UI Design - نفس كل الوظائف، تصميم جديد 100%
import PartsCutterModern from '../components/PartsCutter/PartsCutterModern';
import { PartsPageModern } from '../features/parts/PartsPageModern';
import FormatCropping from '../components/FormatCropping';
import SubtitleSettings from '../components/SubtitleSettings';
import WatermarkSettings from '../components/WatermarkSettings';
import ExportPanel from '../components/ExportPanel';

const PageContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.border};
  background-color: ${props => props.theme.background};
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    flex-direction: column;
    gap: 8px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: transparent;
  color: ${props => props.theme.text};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.surface};
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 10px;
    gap: 3px;
  }
  
  svg {
    width: 14px;
    height: 14px;
    
    @media (max-width: 768px) {
      width: 12px;
      height: 12px;
    }
    
    @media (max-width: 480px) {
      width: 10px;
      height: 10px;
    }
  }
`;

const ProjectTitle = styled.h1`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${props => props.theme.success};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.success}dd;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    padding: 3px 6px;
    font-size: 10px;
    gap: 3px;
  }
  
  svg {
    width: 14px;
    height: 14px;
    
    @media (max-width: 768px) {
      width: 12px;
      height: 12px;
    }
    
    @media (max-width: 480px) {
      width: 10px;
      height: 10px;
    }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    height: calc(100vh - 60px);
  }
`;

const MiddlePanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 300px;

  @media (max-width: 1200px) {
    min-width: auto;
    flex: none;
    height: 45vh;
  }
  
  @media (max-width: 768px) {
    height: 40vh;
    min-height: 200px;
  }
  
  @media (max-width: 480px) {
    height: 35vh;
    min-height: 180px;
  }
`;

const VideoSection = styled.div`
  height: 400px;
  border-bottom: 1px solid ${props => props.theme.border};
  background: #000;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1400px) {
    height: 350px;
  }

  @media (max-width: 1200px) {
    height: 300px;
  }

  @media (max-width: 768px) {
    height: 250px;
  }
  
  @media (max-width: 480px) {
    height: 200px;
  }
`;

const VideoPreview = styled.div`
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  background: ${props => props.theme.surface};
  border-bottom: 1px solid ${props => props.theme.border};
  min-height: 80px;
  
  @media (max-width: 768px) {
    padding: 8px;
    min-height: 60px;
  }
  
  @media (max-width: 480px) {
    padding: 6px;
    min-height: 50px;
  }
`;

const RightPanel = styled.div`
  width: 320px;
  border-left: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 250px;
  max-width: 60%;
  position: relative;

  @media (max-width: 1200px) {
    width: 100%;
    border-left: none;
    border-top: 1px solid ${props => props.theme.border};
    flex: 1;
  }
  
  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  background: transparent;
  cursor: col-resize;
  z-index: 20;
  border-left: 2px solid transparent;
  transition: border-color 0.2s ease;

  &:hover {
    border-left-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}20;
  }

  &:active {
    border-left-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}30;
  }

  @media (max-width: 1200px) {
    display: none;
  }
`;

const RightPanelHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.surface};
`;

const RightPanelTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 6px;
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
  
  svg {
    width: 14px;
    height: 14px;
    
    @media (max-width: 768px) {
      width: 12px;
      height: 12px;
    }
  }
`;

const QuickInfo = styled.div`
  padding: 8px;
  background: ${props => props.theme.surface};
  border-radius: 4px;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    padding: 6px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 4px;
    margin-bottom: 4px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-size: 10px;
  color: ${props => props.theme.textSecondary};
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 12px;
    color: ${props => props.theme.text};
  font-weight: 600;
`;

const ProgressIndicator = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${props => props.theme.border};
`;

const ProgressItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 10px;
`;

const ProgressBar = styled.div`
  width: 50px;
  height: 3px;
  background: ${props => props.theme.border};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.complete ? props.theme.success : props.theme.primary};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${props => props.theme.textSecondary};
`;

const ErrorContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-size: 20px;
  color: ${props => props.theme.danger};
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`;

const TabsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(55px, 1fr));
  gap: 3px;
  margin-top: 6px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
    gap: 2px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(45px, 1fr));
  }
`;

const Tab = styled.button`
  padding: 6px 3px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 4px;
  background: ${props => props.active ? props.theme.primary : props.theme.background};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-align: center;
  min-height: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;

  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.surface};
    border-color: ${props => props.theme.primary};
  }

  svg {
    width: 12px;
    height: 12px;
    
    @media (max-width: 768px) {
      width: 11px;
      height: 11px;
    }
    
    @media (max-width: 480px) {
      width: 10px;
      height: 10px;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 7px;
    padding: 5px 2px;
    min-height: 30px;
  }
  
  @media (max-width: 480px) {
    font-size: 6px;
    padding: 4px 2px;
    min-height: 28px;
  }
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 6px;
  }
`;

const LayoutControls = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 12px;
  padding: 8px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.background};
  align-items: center;

  @media (max-width: 768px) {
    gap: 4px;
    padding: 6px;
    margin-top: 8px;
  }
  
  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 3px;
    padding: 4px;
  }
`;

const LayoutButton = styled.button`
  padding: 4px 8px;
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.border};
  border-radius: 4px;
  background: ${props => props.active ? props.theme.primary : props.theme.background};
  color: ${props => props.active ? 'white' : props.theme.text};
  font-size: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.surface};
    border-color: ${props => props.theme.primary};
  }

  @media (max-width: 768px) {
    padding: 3px 6px;
    font-size: 9px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 4px;
    font-size: 8px;
  }
`;

const EditPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { projects, currentProject, setCurrentProject, updateProject } = useProject();
  
  const [activeTab, setActiveTab] = useState('transcript');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (projectId && projects.length > 0) {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        // Always update currentProject if project data changed (especially videoFile)
        if (!currentProject || currentProject.id !== project.id || 
            (project.videoFile?.url && currentProject.videoFile?.url !== project.videoFile?.url)) {
          console.log('🔄 Updating currentProject with videoFile:', project.videoFile?.url);
          setCurrentProject(project);
          
          // Log what data is loaded
          console.log('Project loaded:', {
            hasTranscript: !!(project.transcript && project.transcript.length > 0),
            hasTranslation: !!(project.translation && project.translation.length > 0),
            hasParts: !!(project.parts && project.parts.length > 0),
            transcriptSegments: project.transcript ? project.transcript.length : 0,
            hasVideoFile: !!project.videoFile,
            videoFileUrl: project.videoFile?.url
          });
        }
      } else if (!project) {
        navigate('/home');
      }
    }
  }, [projectId, projects, currentProject, setCurrentProject, navigate]);

  const handleSave = async () => {
    if (!currentProject || !hasUnsavedChanges) return;
    
    try {
      await updateProject(currentProject.id, currentProject);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  const handleProjectUpdate = async (updates) => {
    console.log('🔄 handleProjectUpdate called with:', updates);
    console.log('🔄 Current project before update:', {
      id: currentProject.id,
      name: currentProject.name,
      partsCount: currentProject.parts?.length || 0,
      groupsCount: currentProject.groups?.length || 0
    });
    
    try {
      // Create updated project immediately
      const updatedProject = { 
        ...currentProject, 
        ...updates, 
        lastModified: new Date().toISOString() 
      };
      
      console.log('🔄 Updated project:', {
        id: updatedProject.id,
        name: updatedProject.name,
        partsCount: updatedProject.parts?.length || 0,
        groupsCount: updatedProject.groups?.length || 0
      });
      
      // Update state immediately
      setCurrentProject(updatedProject);
      
      // Clear previous auto-save timeout
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
      
      // Set new auto-save timeout
      const newTimeout = setTimeout(async () => {
        try {
          setAutoSaveStatus('💾 Saving...');
          
          // Save to localStorage with correct key - with better error handling
          const existingData = localStorage.getItem('beevids-projects');
          const projects = existingData ? JSON.parse(existingData) : [];
          const projectIndex = projects.findIndex(p => p.id === currentProject.id);
          
          if (projectIndex !== -1) {
            // Update existing project
            projects[projectIndex] = updatedProject;
            localStorage.setItem('beevids-projects', JSON.stringify(projects));
            console.log('💾 Project auto-saved to localStorage (updated):', projectIndex);
          } else {
            // Project not found - add it to the list (might be a new project or corruption issue)
            console.warn('⚠️ Project not found in localStorage - adding as new project');
            projects.push(updatedProject);
            localStorage.setItem('beevids-projects', JSON.stringify(projects));
            console.log('💾 Project auto-saved to localStorage (added new):', projects.length - 1);
          }
          
          // Also update via ProjectContext for consistency
          try {
            await updateProject(updatedProject.id, updatedProject);
            console.log('💾 Double-saved: localStorage + ProjectContext');
          } catch (contextError) {
            console.warn('⚠️ ProjectContext save failed, but localStorage saved successfully:', contextError);
          }
          
          setAutoSaveStatus('✅ Auto-saved');
          setHasUnsavedChanges(false);
          
          setTimeout(() => setAutoSaveStatus(''), 2000);
        } catch (error) {
          console.error('Auto-save failed:', error);
          setAutoSaveStatus('❌ Save failed');
          setTimeout(() => setAutoSaveStatus(''), 3000);
        }
      }, 1000); // Auto-save after 1 second of inactivity
      
      setAutoSaveTimeout(newTimeout);
      setHasUnsavedChanges(true);
      setAutoSaveStatus('⏳ Will save...');
      
    } catch (error) {
      console.error('Failed to update project:', error);
      setAutoSaveStatus('❌ Update failed');
      setTimeout(() => setAutoSaveStatus(''), 3000);
    }
  };

  // Enhanced setCurrentTime with debugging
  const handleSeekTo = (time) => {
    console.log('🎯 EditPage: handleSeekTo called with time:', time);
    console.log('🎯 EditPage: Previous currentTime:', currentTime);
    setCurrentTime(time);
    console.log('🎯 EditPage: setCurrentTime called with:', time);
  };

  // Monitor currentTime changes
  useEffect(() => {
    console.log('🕐 EditPage: currentTime changed to:', currentTime);
  }, [currentTime]);

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  // Handle sidebar resizing with more flexible limits
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    
    const screenWidth = window.innerWidth;
    const newWidth = screenWidth - e.clientX;
    const minWidth = 250; // Reduced minimum
    const maxWidth = Math.floor(screenWidth * 0.6); // 60% of screen width
    const clampedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    setSidebarWidth(clampedWidth);
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Preset layout functions
  const setLayoutPreset = (preset) => {
    const screenWidth = window.innerWidth;
    let newWidth;
    
    switch (preset) {
      case 'narrow':
        newWidth = 300;
        break;
      case 'normal':
        newWidth = 400;
        break;
      case 'wide':
        newWidth = Math.floor(screenWidth * 0.35); // 35% of screen
        break;
      case 'half':
        newWidth = Math.floor(screenWidth * 0.5); // 50% of screen
        break;
      default:
        newWidth = 320;
    }
    
    setSidebarWidth(newWidth);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Calculate current layout percentage
  const getCurrentLayoutPercentage = () => {
    const screenWidth = window.innerWidth;
    const percentage = Math.round((sidebarWidth / screenWidth) * 100);
    return percentage;
  };

  const mainTabs = [
    { id: 'transcript', label: 'Text', icon: FileText },
    { id: 'translation', label: 'Lang', icon: Globe },
    { id: 'parts', label: 'Cut', icon: Scissors },
    { id: 'format', label: 'Size', icon: Monitor },
    { id: 'subtitles', label: 'Font', icon: Type },
    { id: 'watermark', label: 'Mark', icon: Droplets },
    { id: 'export', label: 'Save', icon: Upload }
  ];

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressData = () => {
    const transcriptProgress = currentProject.transcript && currentProject.transcript.length > 0 ? 100 : 0;
    const translationProgress = currentProject.translation && currentProject.translation.length > 0 ? 100 : 0;
    const partsProgress = currentProject.parts && currentProject.parts.length > 0 ? 100 : 0;
    
    return [
      { label: 'Transcript', progress: transcriptProgress, complete: transcriptProgress === 100 },
      { label: 'Translation', progress: translationProgress, complete: translationProgress === 100 },
      { label: 'Parts', progress: partsProgress, complete: partsProgress === 100 }
  ];
  };

  if (!projectId) {
    return (
      <ErrorContainer>
        <ErrorTitle>No Project Selected</ErrorTitle>
        <ErrorMessage>Please select a project from the home page to start editing.</ErrorMessage>
      </ErrorContainer>
    );
  }

  if (!currentProject) {
    return (
      <LoadingContainer>
        Loading project...
      </LoadingContainer>
    );
  }

  const renderMainTabContent = () => {
    switch (activeTab) {
      case 'transcript':
        return (
          <TranscriptEditor
            project={currentProject}
            onUpdate={handleProjectUpdate}
            currentTime={currentTime}
            onSeek={handleSeekTo}
          />
        );
      case 'translation':
        return (
          <TranslationPanel
            project={currentProject}
            onUpdate={handleProjectUpdate}
          />
        );
      case 'parts':
        // 🎨 NEW: Modern UI Design - نفس كل الوظائف والخيارات، تصميم جديد 100%
        // ✅ ACTIVATED: الواجهة الجديدة مفعلة الآن!
        return (
          <PartsCutterModern
            project={currentProject}
            onUpdate={handleProjectUpdate}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeekTo}
            isPlaying={isPlaying}
            onPlayPause={setIsPlaying}
          />
        );
        
        // 🔄 OLD: Using original PartsCutter (commented out)
        // إذا أردت العودة للواجهة القديمة، uncomment هذا:
        /*
        return (
          <PartsCutter
            project={currentProject}
            onUpdate={handleProjectUpdate}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeekTo}
            isPlaying={isPlaying}
            onPlayPause={setIsPlaying}
          />
        );
        */
        
        // 🎨 Option 2: PartsPageModern (مختلف قليلاً)
        /*
        return (
          <PartsPageModern
            project={currentProject}
            onUpdate={handleProjectUpdate}
            currentTime={currentTime}
            duration={duration}
            onSeek={handleSeekTo}
            isPlaying={isPlaying}
            onPlayPause={setIsPlaying}
          />
        );
        */
      case 'format':
        return (
          <FormatCropping
            project={currentProject}
            onUpdate={handleProjectUpdate}
          />
        );
      case 'subtitles':
        return (
          <SubtitleSettings
            project={currentProject}
            onUpdate={handleProjectUpdate}
          />
        );
      case 'watermark':
        return (
          <WatermarkSettings
            project={currentProject}
            onUpdate={handleProjectUpdate}
          />
        );
      case 'export':
        return (
          <ExportPanel
            project={currentProject}
            onUpdate={handleProjectUpdate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/home')}>
            <ArrowLeft size={16} />
            Back to Projects
          </BackButton>
          <ProjectTitle>
            {currentProject.name}
          </ProjectTitle>
        </HeaderLeft>
        
        <HeaderRight>
          {autoSaveStatus && (
            <div style={{ 
              marginRight: '12px', 
              fontSize: '12px', 
              color: autoSaveStatus.includes('✅') ? '#28a745' : autoSaveStatus.includes('❌') ? '#dc3545' : '#6c757d',
              fontWeight: '500'
            }}>
              {autoSaveStatus}
            </div>
          )}
          <SaveButton 
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
          >
            <Save size={16} />
            {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
          </SaveButton>
        </HeaderRight>
      </Header>

      <Content>
        <MiddlePanel>
          <VideoSection>
            <VideoPlayer
              videoPath={currentProject.videoPath}
              isPlaying={isPlaying}
              currentTime={currentTime}
              onTimeUpdate={setCurrentTime}
              onDurationChange={setDuration}
              onPlayPause={setIsPlaying}
              project={currentProject}
              onUpdate={handleProjectUpdate}
              selectedLanguage={currentProject.translationLanguage || 'original'}
              subtitleStyle={currentProject.settings?.subtitle || {
                enabled: true,
                fontFamily: 'Arial',
                fontSize: 36,
                fontColor: '#ffffff',
                backgroundColor: '#000000',
                backgroundOpacity: 70,
                maxWordsPerLine: 8,
                keywordHighlighting: false,
                highlightColor: '#ffff00',
                highlightTextColor: '#000000'
              }}
              watermarkSettings={(() => {
                // Find active group or part to get its watermarks
                let activeWatermarks = [];
                
                // 1. Check groups
                const activeGroup = currentProject.groups?.find(g => {
                  // If group has parts, check if any part is active
                  if (g.parts && g.parts.length > 0) {
                    return g.parts.some(p => currentTime >= p.startTime - 0.1 && currentTime <= p.endTime + 0.1);
                  }
                  // If group has segments, check if any segment is active
                  if (g.segments && g.segments.length > 0) {
                    return g.segments.some(sid => {
                      const seg = currentProject.transcript?.find(t => t.id === sid);
                      return seg && currentTime >= seg.startTime - 0.1 && currentTime <= seg.endTime + 0.1;
                    });
                  }
                  return false;
                });
                
                const target = activeGroup || currentProject.parts?.find(p => currentTime >= p.startTime - 0.1 && currentTime <= p.endTime + 0.1);
                
                if (target) {
                  console.log('💧 Player: Active target found for watermarks:', target.name || target.id);
                  const wmNames = target.watermarkNames || (typeof target.watermark === 'string' ? [target.watermark] : []);
                  if (wmNames.length > 0) {
                    try {
                      const savedPresets = JSON.parse(localStorage.getItem('watermarkPresets') || '[]');
                      wmNames.forEach(nameId => {
                        if (!nameId || nameId === 'none') return;
                        const preset = savedPresets.find(p => p.id === nameId || p.name === nameId);
                        if (preset?.settings) {
                          activeWatermarks.push({ ...preset.settings, _presetId: nameId });
                        }
                      });
                    } catch (e) {}
                  } else if (target.watermarkSettings) {
                    activeWatermarks.push(target.watermarkSettings);
                  }
                }
                
                console.log('💧 Player: Resolved watermarks count:', activeWatermarks.length);
                
                // Fallback to project global if nothing found
                if (activeWatermarks.length === 0) {
                  return currentProject.settings?.watermark || { enabled: false };
                }
                
                return activeWatermarks;
              })()}
            />
          </VideoSection>

          <VideoPreview>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '6px'
            }}>
              <div style={{ 
                fontSize: 'clamp(10px, 2vw, 12px)', 
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Settings size={10} />
                Tools
              </div>
              <div style={{ 
                fontSize: 'clamp(9px, 1.5vw, 11px)', 
                color: '#666' 
              }}>
                {formatDuration(currentTime)} / {formatDuration(duration)}
              </div>
            </div>
            
            {/* Project Quick Info */}
            <QuickInfo>
              <div style={{ marginBottom: '6px', fontSize: '10px', fontWeight: '600', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Monitor size={10} />
                Project
              </div>
              <InfoGrid>
                <InfoItem>
                  <InfoLabel>Time</InfoLabel>
                  <InfoValue>{formatDuration(duration)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Now</InfoLabel>
                  <InfoValue>{formatDuration(currentTime)}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>Parts</InfoLabel>
                  <InfoValue>{currentProject.transcript ? currentProject.transcript.length : 0}</InfoValue>
                </InfoItem>
                <InfoItem>
                  <InfoLabel>State</InfoLabel>
                  <InfoValue>{currentProject.translation ? 'Trans' : 'Orig'}</InfoValue>
                </InfoItem>
              </InfoGrid>
              
              <ProgressIndicator>
                <div style={{ fontSize: '9px', fontWeight: '600', color: '#666', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Download size={8} />
                  Progress
                </div>
                {getProgressData().map((item, index) => (
                  <ProgressItem key={index}>
                    <span style={{ fontSize: '9px' }}>{item.label}</span>
                    <ProgressBar>
                      <ProgressFill progress={item.progress} complete={item.complete} />
                    </ProgressBar>
                  </ProgressItem>
                ))}
              </ProgressIndicator>
            </QuickInfo>

            {/* Layout Controls */}
            {window.innerWidth > 1200 && (
              <LayoutControls>
                <div style={{ 
                  fontSize: '11px', 
                  fontWeight: '600',
                  color: '#333',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  ⚖️ Layout: {getCurrentLayoutPercentage()}%
                </div>
                <LayoutButton 
                  active={sidebarWidth <= 320}
                  onClick={() => setLayoutPreset('narrow')}
                >
                  🔍 Narrow
                </LayoutButton>
                <LayoutButton 
                  active={sidebarWidth > 320 && sidebarWidth <= 420}
                  onClick={() => setLayoutPreset('normal')}
                >
                  📱 Normal
                </LayoutButton>
                <LayoutButton 
                  active={sidebarWidth > 420 && getCurrentLayoutPercentage() < 45}
                  onClick={() => setLayoutPreset('wide')}
                >
                  📺 Wide
                </LayoutButton>
                <LayoutButton 
                  active={getCurrentLayoutPercentage() >= 45}
                  onClick={() => setLayoutPreset('half')}
                >
                  ⚖️ 50/50
                </LayoutButton>
              </LayoutControls>
            )}
          </VideoPreview>
        </MiddlePanel>

        <RightPanel style={{ width: window.innerWidth > 1200 ? `${sidebarWidth}px` : undefined }}>
          <ResizeHandle onMouseDown={handleMouseDown} />
          
          <RightPanelHeader>
            <RightPanelTitle>
              <Settings />
              Editing Tools
            </RightPanelTitle>

          <TabsContainer>
              {mainTabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
              <Tab
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                    title={tab.id.charAt(0).toUpperCase() + tab.id.slice(1)}
              >
                    <IconComponent />
                {tab.label}
              </Tab>
                );
              })}
          </TabsContainer>
          </RightPanelHeader>

          <TabContent>
            {renderMainTabContent()}
          </TabContent>
        </RightPanel>
      </Content>
    </PageContainer>
  );
};

export default EditPage;